import React, { useEffect, useRef, useState } from "react";
import { Camera, Video, X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, inputCls } from "@/components/common";
import { ClaimLookup } from "@/components/ClaimStatus";

type Claim = { id: string; token: string; order_number: string };

async function uploadVideo(c: Claim, file: File, onProgress: (n: number) => void) {
  const q = { params: { t: c.token } };
  const { upload_id, chunk_size } = (await api.post(`/warranty/claims/${c.id}/video/init`, { filename: file.name, size: file.size, content_type: file.type }, q)).data;
  const n = Math.ceil(file.size / chunk_size);
  for (let i = 0; i < n; i++) {
    await api.put(`/warranty/claims/${c.id}/video/${upload_id}/${i}`, file.slice(i * chunk_size, (i + 1) * chunk_size), { ...q, headers: { "Content-Type": "application/octet-stream" } });
    onProgress(Math.round(((i + 1) / n) * 100));
  }
  return (await api.post(`/warranty/claims/${c.id}/video/${upload_id}/complete`, null, q)).data;
}

function OrderFields({ f, setF, orders, other, setOther }: any) {
  const order = orders.find((o: any) => o.number === f.order_number);
  const items = (order?.items || []).map((i: any) => i.name).filter(Boolean);
  const pick = (v: string) => { if (v === "__other") { setOther(true); setF({ ...f, order_number: "", item: "" }); } else setF({ ...f, order_number: v, item: "" }); };
  if (orders.length && !other) return (
    <>
      <Field label="Order"><select value={f.order_number} onChange={(e) => pick(e.target.value)} className={inputCls} data-testid="claim-order-select">
        {orders.map((o: any) => <option key={o.number} value={o.number}>{o.number} · {new Date(o.created_at).toLocaleDateString("en-AU")}</option>)}
        <option value="__other">Another order number…</option>
      </select></Field>
      <Field label="Which piece?"><select required value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} className={inputCls} data-testid="claim-item-select">
        <option value="">Choose a piece</option>{items.map((n: string) => <option key={n}>{n}</option>)}
      </select></Field>
    </>
  );
  return (
    <>
      <Field label="Order number"><input required value={f.order_number} onChange={(e) => setF({ ...f, order_number: e.target.value })} placeholder="SC-123456" className={inputCls} data-testid="claim-order-input" />
        {orders.length > 0 && <button type="button" onClick={() => { setOther(false); setF({ ...f, order_number: orders[0].number, item: "" }); }} className="mt-1 text-xs underline" data-testid="claim-order-back">Choose from my orders</button>}</Field>
      {!orders.length && <Field label="Email used at checkout"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="claim-email-input" /></Field>}
      <div className={orders.length ? "" : "sm:col-span-2"}><Field label="Which piece?"><input required value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} placeholder="e.g. Oval Solitaire Ring" className={inputCls} data-testid="claim-item-input" /></Field></div>
    </>
  );
}

function StartForm({ onStart }: { onStart: (c: Claim) => void }) {
  const { user } = useAuth() as any;
  const [orders, setOrders] = useState<any[]>([]);
  const [f, setF] = useState<any>({ order_number: "", email: "", item: "", issue: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [other, setOther] = useState(false);
  useEffect(() => {
    if (!user) return;
    api.get("/orders").then((r) => {
      const ok = r.data.filter((o: any) => !["cancelled", "refunded"].includes(o.status) && ["paid", "awaiting_transfer"].includes(o.payment_status));
      setOrders(ok); if (ok[0]) setF((x: any) => ({ ...x, order_number: ok[0].number }));
    }).catch(() => {});
  }, [user]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try { onStart((await api.post("/warranty/claims", { ...f, email: f.email || undefined, phone: f.phone || undefined })).data); }
    catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5 border border-neutral-200 bg-white p-6" data-testid="claim-start-form">
      {!user && <p className="sm:col-span-2 text-xs text-neutral-500">Signed in? Your orders will be listed for you.</p>}
      <OrderFields f={f} setF={setF} orders={orders} other={other} setOther={setOther} />
      <div className="sm:col-span-2"><Field label="What happened? Please describe the issue"><textarea required minLength={10} rows={4} value={f.issue} onChange={(e) => setF({ ...f, issue: e.target.value })} className={inputCls} data-testid="claim-issue" /></Field></div>
      <Field label="Phone (optional)"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} data-testid="claim-phone" /></Field>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" onChange={(e) => setF({ ...f, website: e.target.value })} />
      <button disabled={busy} className="btn-ink sm:col-span-2 py-4 text-[11px] tracking-[0.25em] uppercase disabled:opacity-60" data-testid="claim-start-submit">{busy ? "Checking your order…" : "Next: Add Photos or Videos"}</button>
    </form>
  );
}

function Uploads({ c, onDone }: { c: Claim; onDone: (n: string) => void }) {
  const [media, setMedia] = useState<any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const pRef = useRef<HTMLInputElement>(null);
  const vRef = useRef<HTMLInputElement>(null);
  const q = { params: { t: c.token } };
  const addPhotos = async (files: FileList | null) => {
    for (const file of Array.from(files || [])) {
      setBusy(`Uploading ${file.name}…`);
      const fd = new FormData(); fd.append("file", file);
      try { const m = (await api.post(`/warranty/claims/${c.id}/photo`, fd, q)).data; setMedia((x) => [...x, { ...m, preview: URL.createObjectURL(file) }]); }
      catch (e) { toast.error(errMsg(e)); }
    }
    setBusy(null);
  };
  const addVideo = async (file?: File) => {
    if (!file) return;
    try { const m = await uploadVideo(c, file, (n) => setBusy(`Uploading video… ${n}%`)); setMedia((x) => [...x, m]); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  const remove = async (id: string) => { await api.delete(`/warranty/claims/${c.id}/media/${id}`, q).catch(() => {}); setMedia((x) => x.filter((m) => m.id !== id)); };
  const submit = async () => { setBusy("Sending your claim…"); try { onDone((await api.post(`/warranty/claims/${c.id}/submit`, null, q)).data.number); } catch (e) { toast.error(errMsg(e)); setBusy(null); } };
  const photos = media.filter((m) => m.kind === "photo").length, videos = media.length - photos;
  return (
    <div className="border border-neutral-200 bg-white p-6 space-y-5" data-testid="claim-uploads">
      <p className="text-sm">Order <strong>{c.order_number}</strong> found. Add clear photos or a short video showing the issue — up to 6 photos (10 MB each) and 2 videos (200 MB each).</p>
      <div className="flex flex-wrap gap-3">
        <button type="button" disabled={!!busy || photos >= 6} onClick={() => pRef.current?.click()} className="btn-line inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.2em] uppercase disabled:opacity-50" data-testid="claim-add-photo"><Camera size={15} />Add photos</button>
        <button type="button" disabled={!!busy || videos >= 2} onClick={() => vRef.current?.click()} className="btn-line inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.2em] uppercase disabled:opacity-50" data-testid="claim-add-video"><Video size={15} />Add a video</button>
        <input ref={pRef} type="file" accept="image/*" multiple hidden onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} data-testid="claim-photo-input" />
        <input ref={vRef} type="file" accept="video/mp4,video/quicktime,video/webm" hidden onChange={(e) => { addVideo(e.target.files?.[0]); e.target.value = ""; }} data-testid="claim-video-input" />
      </div>
      {busy && <p className="text-xs text-neutral-600 inline-flex items-center gap-2" data-testid="claim-busy"><Loader2 size={14} className="animate-spin" />{busy}</p>}
      {media.length > 0 && (
        <ul className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="claim-media-list">
          {media.map((m) => (
            <li key={m.id} className="relative aspect-square bg-neutral-100 grid place-items-center text-[10px] text-neutral-600 overflow-hidden" data-testid={`claim-media-${m.id}`}>
              {m.preview ? <img src={m.preview} alt="" className="w-full h-full object-cover" /> : <span className="px-2 text-center"><Video size={18} className="mx-auto mb-1" />{m.name}</span>}
              <button type="button" onClick={() => remove(m.id)} aria-label="Remove" className="absolute top-1 right-1 bg-white/90 rounded-full p-1" data-testid={`claim-media-remove-${m.id}`}><X size={12} /></button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" disabled={!!busy || !media.length} onClick={submit} className="btn-ink w-full py-4 text-[11px] tracking-[0.25em] uppercase disabled:opacity-50" data-testid="claim-submit">Send My Warranty Claim</button>
    </div>
  );
}

export function WarrantyClaimBlock({ b }: any) {
  const [claim, setClaim] = useState<Claim | null>(null);
  const [number, setNumber] = useState("");
  return (
    <section id="warranty-claim" className="max-w-3xl mx-auto px-5 md:px-12 py-10 scroll-mt-48" data-testid="warranty-claim-block">
      {b.heading && <h2 className="font-display uppercase tracking-[0.08em] text-2xl md:text-3xl text-center">{b.heading}</h2>}
      {b.text && <p className="text-center text-neutral-600 mt-3 mb-6">{b.text}</p>}
      {number ? (
        <div className="border border-neutral-200 bg-white p-6 text-sm flex gap-3" data-testid="claim-success">
          <CheckCircle2 className="shrink-0 text-green-700" size={20} />
          <div><p>Thank you — your claim <strong>{number}</strong> has been sent and we've emailed you a confirmation. Our team will review it and be in touch, usually within one business day.</p>
            <button type="button" onClick={() => { setNumber(""); setClaim(null); }} className="mt-3 text-xs underline" data-testid="claim-start-another">Start another claim</button></div>
        </div>
      ) : claim ? <Uploads c={claim} onDone={setNumber} /> : <StartForm onStart={setClaim} />}
      <ClaimLookup />
    </section>
  );
}
